import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useGlobalAssistant } from '@/contexts/GlobalAssistantContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useEvalDetail } from './hooks/useEvalDetail';
import { StandardEvalDetail } from './StandardEvalDetail';
import { OptimizationEvalDetail } from './OptimizationEvalDetail';

/**
 * EvalDetail acts as a router component that determines whether to show
 * the StandardEvalDetail or OptimizationEvalDetail based on eval configuration.
 */
export function EvalDetail() {
  const { evalId } = useParams<{ evalId: string }>();
  const { registerPageContext } = useGlobalAssistant();

  const hook = useEvalDetail({ evalId });
  const { evalData, runs, loading, error } = hook;

  // Register eval context for global assistant
  useEffect(() => {
    registerPageContext({
      contextType: 'evals',
      evals: {
        current_eval_id: evalId,
        eval_name: evalData?.name,
        eval_agent: evalData?.agent,
        is_optimization: evalData?.optimization?.enabled ?? false,
        variants_count: evalData?.variants?.length || 0,
        runs_count: runs.length,
        has_running_runs: runs.some((r) => r.status === 'running'),
      },
    });
  }, [registerPageContext, evalId, evalData, runs]);

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error && !evalData) {
    return (
      <div className="container max-w-6xl py-8">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Not found state
  if (!evalData) {
    return (
      <div className="container max-w-6xl py-8">
        <Alert variant="destructive">
          <AlertDescription>Evaluation not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Route to appropriate detail component based on eval type
  if (evalData.optimization?.enabled) {
    return <OptimizationEvalDetail evalId={evalId!} hook={hook} />;
  }

  return <StandardEvalDetail evalId={evalId!} hook={hook} />;
}

export default EvalDetail;
